import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export interface AgentCapability {
  activation?: 'static' | 'selectable' | 'hidden' | string;
  selected?: boolean;
  enabledToolIds?: string[];
}

export interface AgentRosterEntry {
  id: string;
  name: string;
  description?: string;
  active?: boolean;
  available?: boolean;
  isConfigured?: boolean | null;
  capabilityIds?: string[];
  capabilities?: Record<string, AgentCapability>;
}

export type AgentRoster = Record<string, AgentRosterEntry>;

export function useAgentRoster() {
  return useQuery({
    queryKey: ['agent-roster'],
    queryFn: async (): Promise<AgentRoster> => {
      const res = await api.get<AgentRoster | unknown>('/api/agents');
      if (!res || typeof res !== 'object' || Array.isArray(res)) return {};
      return res as AgentRoster;
    },
    staleTime: 30_000,
  });
}

/**
 * Tool from the integrations `/api/v1/tools` catalogue. This is the full
 * tenant-scoped tool list — every tool the workspace knows about, with
 * display name + description + capability link. Distinct from "tools
 * currently enabled on agent X for user Y" (which lives on the roster).
 */
export interface CatalogTool {
  toolId: string;
  capabilityId: string | null;
  connectorId: string | null;
  name: string;
  description: string | null;
  featureFlag: string | null;
  available: boolean;
}

export function useToolCatalog() {
  return useQuery({
    queryKey: ['tool-catalog'],
    queryFn: async (): Promise<CatalogTool[]> => {
      const res = await api.get<{ tools?: CatalogTool[] } | CatalogTool[]>('/api/admin/tools');
      if (Array.isArray(res)) return res;
      return res?.tools ?? [];
    },
    staleTime: 5 * 60_000,
  });
}
